import { ui } from '@utils/i18n/uiCopy'
// import disco from '@assets/topbar/disco1.png' // Guardar oculto temporalmente
import deshacerIcon from '@assets/atemporal/deshacer.png'
import rehacerIcon from '@assets/atemporal/rehacer.png'
import duplicarIcon from '@assets/sidebar/Duplicar.png'
import eliminarIcon from '@assets/nuevos-iconos/Eliminar2.png'
import agregarDocumento from '@assets/nuevos-iconos/agregarIcon.png'
import calculatorIcon from '@assets/nuevos-iconos/calculadora.png'
import { useUndoRedo } from '@hooks/useUndoRedo'
import { useAppStore, useUiPresentationStore } from '@store'
import {
  duplicateSelectedLinkedUnitAtRightEnd,
  resolveSelectedLinkedUnit,
} from '@utils/sceneObjects/items/duplicateLinkedUnit'
import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

type TouchActionBarProps = {
  onOpenBudget?: () => void
}

/**
 * Acciones primarias flotantes para touch.
 * Orden alineado con Desktop: nuevo, presupuesto, undo/redo, dup/delete.
 * (Guardar oculto temporalmente)
 */
export function TouchActionBar({ onOpenBudget }: TouchActionBarProps) {
  const navigate = useNavigate()
  const { canUndo, canRedo, performUndo, performRedo } = useUndoRedo()
  const deleteSelectedObjects = useAppStore.use.deleteSelectedObjects()
  const openModal = useAppStore.use.openModal()
  const closeSheet = useUiPresentationStore((s) => s.closeSheet)
  const setMode = useAppStore.use.setMode()
  const mode = useAppStore.use.mode()
  const esMio = useAppStore((s) => s.esMio)
  const tipoUsuario = useAppStore((s) => s.tipoUsuario)
  const items = useAppStore.use.items()
  const currentLayer = useAppStore.use.currentLayer()
  const layers = useAppStore.use.layers()
  const selectedItems = layers[currentLayer]?.selected || new Set()
  const currentObjectID = useAppStore.use.currentObjectID()
  const [isDuplicating, setIsDuplicating] = useState(false)

  const canDuplicate = Boolean(
    resolveSelectedLinkedUnit(items, selectedItems, currentObjectID),
  )
  const hasSelection = selectedItems.size > 0

  // Guardar oculto temporalmente
  // const handleSave = () => {
  //   if (!(esMio || tipoUsuario === 'A' || tipoUsuario === 'U' || tipoUsuario === 'G')) {
  //     openModal()
  //     return
  //   }
  //   sessionStorage.setItem('pendingSave', 'true')
  //   navigate('/planner/proyectos')
  // }

  const handleNewDesign = () => {
    closeSheet()
    if (!(esMio || tipoUsuario === 'A' || tipoUsuario === 'U' || tipoUsuario === 'G')) {
      openModal()
      return
    }
    if (mode === 'MODE_3D_VIEW') setMode('MODE_IDLE')
    useAppStore.getState().setFrontalActive?.(false)
    // Sin modal: NewDesign ejecuta "No" (nuevo en blanco) al navegar.
    navigate('/planner/newDesign')
  }

  const handleOpenBudget = () => {
    closeSheet()
    requestAnimationFrame(() => {
      onOpenBudget?.()
    })
  }

  const handleDuplicate = async () => {
    if (!canDuplicate || isDuplicating) return
    setIsDuplicating(true)
    try {
      await duplicateSelectedLinkedUnitAtRightEnd()
    } finally {
      setIsDuplicating(false)
    }
  }

  return (
    <div className='pointer-events-none fixed right-3 top-14 z-[65] flex flex-col gap-2'>
      <div className='pointer-events-auto flex max-h-[calc(100dvh-8rem)] flex-col gap-1.5 overflow-y-auto rounded-2xl border border-colorAtemporal bg-white/95 p-1.5 shadow-lg backdrop-blur-sm'>
        {/* Guardar: oculto temporalmente (producto no lo necesita ahora)
        <IconBtn label='Guardar' onClick={handleSave}>
          <img src={disco} alt='' className='h-5 w-5' draggable={false} />
        </IconBtn>
        */}
        <IconBtn label='Nuevo diseño' onClick={handleNewDesign}>
          <img src={agregarDocumento} alt='' className='h-5 w-5' draggable={false} />
        </IconBtn>
        <IconBtn label={ui('budget')} onClick={handleOpenBudget}>
          <img src={calculatorIcon} alt='' className='h-5 w-5' draggable={false} />
        </IconBtn>
        <IconBtn label={ui('undo')} onClick={performUndo} disabled={!canUndo}>
          <img src={deshacerIcon} alt='' className='h-5 w-5' draggable={false} />
        </IconBtn>
        <IconBtn label='Rehacer' onClick={performRedo} disabled={!canRedo}>
          <img src={rehacerIcon} alt='' className='h-5 w-5' draggable={false} />
        </IconBtn>
        {hasSelection && (
          <>
            <IconBtn
              label='Duplicar'
              onClick={handleDuplicate}
              disabled={!canDuplicate || isDuplicating}
            >
              <img src={duplicarIcon} alt='' className='h-5 w-5' draggable={false} />
            </IconBtn>
            <IconBtn label='Eliminar' onClick={() => deleteSelectedObjects()}>
              <img src={eliminarIcon} alt='' className='h-5 w-5' draggable={false} />
            </IconBtn>
          </>
        )}
      </div>
    </div>
  )
}

function IconBtn({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type='button'
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-colorAtemporal/40 bg-C/Color1 transition-all active:scale-95 ${
        disabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-[#F5F0E4]'
      }`}
    >
      {children}
    </button>
  )
}

/** Contenido del sheet "Más": acciones menos frecuentes. */
export function MoreActionsSheet({ onOpenBudget }: { onOpenBudget?: () => void }) {
  const navigate = useNavigate()
  const openModal = useAppStore.use.openModal()
  const closeSheet = useUiPresentationStore((s) => s.closeSheet)
  const hideDimentions3D = useAppStore((s) => s.hideDimentions3D)
  const setHideDimentions3D = useAppStore((s) => s.setHideDimentions3D)
  const setMode = useAppStore.use.setMode()
  const mode = useAppStore.use.mode()
  const esMio = useAppStore((s) => s.esMio)
  const tipoUsuario = useAppStore((s) => s.tipoUsuario)

  const handleNewDesign = () => {
    closeSheet()
    if (!(esMio || tipoUsuario === 'A' || tipoUsuario === 'U' || tipoUsuario === 'G')) {
      openModal()
      return
    }
    if (mode === 'MODE_3D_VIEW') setMode('MODE_IDLE')
    useAppStore.getState().setFrontalActive?.(false)
    // Sin modal: NewDesign ejecuta "No" (nuevo en blanco) al navegar.
    navigate('/planner/newDesign')
  }

  const handleOpenBudget = () => {
    closeSheet()
    requestAnimationFrame(() => {
      onOpenBudget?.()
    })
  }

  return (
    <div className='flex flex-col gap-2 p-4 pb-6'>
      <MoreRow label='Nuevo diseño' onClick={handleNewDesign} />
      {/* Proyectos: oculto temporalmente en Más (dispositivos pequeños) */}
      {/*
      <MoreRow
        label='Proyectos'
        onClick={() => {
          closeSheet()
          navigate('/planner/proyectos')
        }}
      />
      */}
      <MoreRow
        label={hideDimentions3D ? ui('showDimensions') : ui('hideDimensions')}
        onClick={() => {
          setHideDimentions3D?.(!hideDimentions3D)
        }}
      />
      <MoreRow label={ui('budget')} onClick={handleOpenBudget} />
    </div>
  )
}

function MoreRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className='flex h-12 w-full items-center rounded-xl border border-[#E8DFC8] bg-white px-4 text-left text-sm font-medium text-C/Color3 transition-colors active:bg-[#F5F0E4]'
    >
      {label}
    </button>
  )
}
